import { newEstimateValidUntil } from '@/features/quotes/estimateDefaults';

/**
 * First-run proposal wizard — the pure half.
 *
 * Why this exists (prod, 2026-09-01, 120 trial signups): 100 never returned
 * after the signup session, 57 never created a customer, 3 ever sent a
 * proposal. The first session is the only session, so it is spent getting one
 * proposal into the owner's own inbox. Everything here is data + arithmetic so
 * the step order, resume logic and estimate math can be unit-tested without a
 * DOM.
 */

export type WizardStep = 'demo' | 'brand' | 'customer' | 'mockup' | 'estimate' | 'lumen' | 'send' | 'done';
export type WizardStatus = 'active' | 'completed' | 'dismissed';

/** Row shape of public.org_onboarding_wizard as returned by the RPCs. */
export interface WizardRow {
  organization_id: string;
  version: number;
  /** Stamped onto every artifact the wizard creates; recovery matches on it. */
  run_key: string;
  status: WizardStatus;
  current_step: WizardStep;
  sample_used: boolean;
  customer_id: string | null;
  mockup_id: string | null;
  quote_id: string | null;
  proposal_id: string | null;
  step_events: Array<{ step: string; action: string; at: string }>;
  /** When the org booked its demo from the walkthrough (column keeps its historical name). */
  training_booked_at?: string | null;
  started_at: string;
  completed_at: string | null;
  dismissed_at: string | null;
  updated_at: string;
}

export interface WizardStatusPayload {
  eligible: boolean;
  wizard: WizardRow | null;
}

/**
 * The screens, in order. 'done' is the terminal screen, not a task.
 *
 * 'demo' comes first (2026-09-17): the offer to book a demo with customer
 * success is the first thing a new owner sees, before any setup.
 */
export const WIZARD_STEPS: WizardStep[] = ['demo', 'brand', 'customer', 'mockup', 'estimate', 'lumen', 'send', 'done'];

/** The task steps a progress indicator counts (everything but 'done'). */
export const TASK_STEPS: WizardStep[] = WIZARD_STEPS.filter((s) => s !== 'done');

export const STEP_LABEL: Record<WizardStep, string> = {
  demo: 'Book a demo',
  brand: 'Your brand',
  customer: 'First customer',
  mockup: 'Mockup',
  estimate: 'Estimate',
  lumen: 'Meet Lumen',
  send: 'Send it',
  done: 'Done',
};

export function stepIndex(step: WizardStep): number {
  return WIZARD_STEPS.indexOf(step);
}

export function nextStep(step: WizardStep): WizardStep {
  const i = stepIndex(step);
  return WIZARD_STEPS[Math.min(i + 1, WIZARD_STEPS.length - 1)];
}

export function prevStep(step: WizardStep): WizardStep {
  const i = stepIndex(step);
  return WIZARD_STEPS[Math.max(i - 1, 0)];
}

export function isWizardStep(value: unknown): value is WizardStep {
  return typeof value === 'string' && (WIZARD_STEPS as string[]).includes(value);
}

/**
 * Should "/" send this user into the wizard right now?
 *
 *   • an active row always resumes — that is the whole point of persisting it
 *   • no row + eligible (admin of an org with no work yet) starts it
 *   • completed / dismissed never auto-open again; the dashboard card is the
 *     way back in
 */
export function shouldAutoOpen(payload: WizardStatusPayload | null | undefined): boolean {
  if (!payload) return false;
  // `eligible` is the server's word on the plan and the role, for a row that
  // exists as much as for one that does not: a trial that lapsed onto Free
  // mid-walkthrough must not keep bouncing the owner back in.
  if (!payload.eligible) return false;
  if (payload.wizard) return payload.wizard.status === 'active';
  return true;
}

/**
 * The redirect-loop guard (Sentry LIGHTMASTER-WEB-1Y, 11 new signups bounced
 * "/" ⇄ "/welcome" ~25 times a second until the browser stopped them).
 *
 * "/" and "/welcome" read the same cached status. A failed refetch (booking a
 * demo invalidates it; an iPhone that switched apps drops the request) leaves
 * the OLD data in the cache with isError set: "/welcome" left on the error,
 * "/" re-opened on the stale "active" data, forever. So:
 *   • "/" never auto-opens on an errored status, and opens it at most once per
 *     page load — after that the dashboard's resume card is the way back in;
 *   • "/welcome" leaves on an error only when it has no data to work from.
 */
let wizardShownThisPage = false;

export function markWizardShown(): void {
  wizardShownThisPage = true;
}

/** Test seam: a fresh page load. */
export function resetWizardShownForTests(): void {
  wizardShownThisPage = false;
}

export function shouldRedirectToWizard(
  payload: WizardStatusPayload | null | undefined,
  isError: boolean,
): boolean {
  if (isError || wizardShownThisPage) return false;
  return shouldAutoOpen(payload);
}

export function welcomeShouldLeaveOnError(isError: boolean, payload: WizardStatusPayload | null | undefined): boolean {
  return isError && !payload;
}

/**
 * The wizard URL for a step: what a sign-in after a lost session returns to
 * (#1657). `forUser` names whose step it is, so a different account signing in
 * from the same link resumes its own stored step instead.
 */
export function wizardStepPath(step: WizardStep, forUser?: string | null): string {
  return `/welcome?step=${step}${forUser ? `&for=${encodeURIComponent(forUser)}` : ''}`;
}

/** Sign in again and come back to the step that was on screen. */
export function recoveryRedirect(step: WizardStep, forUser?: string | null): string {
  return `/auth?redirect=${encodeURIComponent(wizardStepPath(step, forUser))}`;
}

/**
 * Where to land when the wizard opens. A deep link (`?step=`) wins when it is a
 * real step; otherwise the stored step. A row that is already 'completed'
 * lands on the done screen no matter what — its artifacts are the answer.
 */
export function resumeStep(row: WizardRow | null | undefined, requested?: string | null): WizardStep {
  if (row?.status === 'completed') return 'done';
  if (isWizardStep(requested) && requested !== 'done') return requested;
  // The trial trigger arms the row before the owner ever arrives, at the
  // column default. A row nobody has visited is a fresh start, and a fresh
  // start opens on the demo offer whatever the stored step says.
  if (row && (row.step_events?.length ?? 0) === 0) return 'demo';
  if (row && isWizardStep(row.current_step)) return row.current_step;
  return 'demo';
}

/**
 * The signup trigger names every new org "<Full name>'s Business". That is a
 * placeholder, not a brand — offer an empty field rather than a wrong prefill.
 */
export function suggestedBusinessName(orgName: string | null | undefined, businessName: string | null | undefined): string {
  const explicit = businessName?.trim();
  if (explicit) return explicit;
  const name = orgName?.trim() ?? '';
  if (/'s Business$/i.test(name) || /’s Business$/i.test(name)) return '';
  return name;
}

// ---------------------------------------------------------------------------
// Estimate math — one roofline line, no tax. The add-on it used to carry is
// now Lumen's to add on the next step.
// ---------------------------------------------------------------------------

export interface EstimateDraft {
  feet: number;
  ratePerFoot: number;
}

// Measured against production on 2026-09-03, not guessed. Two independent
// sources agree on the rate, which is why it is the one number here we can
// state a band for on screen:
//
//   quote lines   27 orgs / 65 lines   p25 $5.75   median $7.00   p75 $9.38
//   cost catalogs 55 orgs / 119 items  p25 $6.00   median $7.00   p75 $8.88
//
// (Roofline-ish descriptions, 20+ feet, $1-100/ft, wizard-created and internal
// orgs excluded so our own default cannot vote for itself.)
//
// Feet: real median is 133 (p25 100, p75 220). 150 sits inside that range and
// reads as a round placeholder rather than an oddly precise claim about their
// house, which is the point — see the step's copy.
//
// The wreath package used to live here as a toggle. It is now the line Lumen
// adds on the next step (LUMEN_FORGOTTEN_LINE), which is worth more in two
// ways: this step drops to a single decision, and Lumen gets to demonstrate
// itself by ADDING money to the job rather than by adding a $70 utility line.
export const ESTIMATE_DEFAULTS: EstimateDraft = {
  feet: 150,
  ratePerFoot: 7,
};

/** The band shown next to the rate field, from the percentiles above. */
export const RATE_BAND = { low: 6, high: 9 } as const;

/**
 * Is the wizard's first customer the owner themselves?
 *
 * The whole reassurance rests on this: telling someone "only you will see
 * this" when they typed a real lead's address would be a lie, and a worse one
 * than saying nothing. Email is the only identity both records share.
 */
export function isSelfCustomer(
  customerEmail: string | null | undefined,
  ownerEmail: string | null | undefined,
): boolean {
  const a = customerEmail?.trim().toLowerCase();
  const b = ownerEmail?.trim().toLowerCase();
  return !!a && !!b && a === b;
}

export interface EstimateLine {
  description: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  item_type: 'service' | 'product';
  sort_order: number;
}

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function estimateLines(draft: EstimateDraft): EstimateLine[] {
  const feet = Math.max(0, Number(draft.feet) || 0);
  const rate = Math.max(0, Number(draft.ratePerFoot) || 0);
  return [
    {
      description: 'Roofline lighting — warm white C9, professionally installed, removed and stored',
      quantity: feet,
      unit_price: rate,
      total_price: round2(feet * rate),
      item_type: 'service',
      sort_order: 0,
    },
  ];
}

export function estimateTotal(draft: EstimateDraft): number {
  return round2(estimateLines(draft).reduce((sum, l) => sum + l.total_price, 0));
}

export function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

/** The marker the wizard puts in an estimate's notes so recovery can find it by run. */
export function estimateNotes(runKey: string | null | undefined): string {
  return `Created by the welcome walkthrough — edit anything here, it is a normal estimate.${runKey ? ` [wizard-run ${runKey}]` : ''}`;
}

/**
 * ISO date (YYYY-MM-DD) the estimate builder would give a new estimate: today
 * in the org's timezone plus its "Estimates valid for" days (30 by default).
 */
export function validUntil(org?: Parameters<typeof newEstimateValidUntil>[1], from: Date = new Date()): string {
  return newEstimateValidUntil(from, org);
}

/** "Holiday Lighting 2026" — the season the proposal is for. */
export function seasonYear(now: Date = new Date()): number {
  return now.getFullYear();
}

/**
 * Photo picker hint. The AI normalizer decodes JPEG/PNG/WebP and converts
 * HEIC/HEIF (#1043: natively in Safari, with the lazy HEIC decoder in Chrome
 * and Firefox), so a .heic chosen on a computer is accepted too. Keep the
 * two in lockstep.
 */
/** Picker types; photoAccept() adds HEIC/HEIF on computers (iPhones hand over JPEG). */
export const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';

export function photoTypeSupported(file: { type: string; name: string }): boolean {
  if (['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(file.type)) return true;
  // Some pickers hand over an empty type (or octet-stream) with a good extension.
  return (!file.type || file.type === 'application/octet-stream') && /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
}
